import { useEffect } from 'react';

/**
 * Hook for hash-based navigation.
 * - On mount, scrolls to the section matching the URL hash.
 * - Updates the URL hash when scrolling to a section.
 * - Listens for hashchange events (back/forward buttons).
 */
export function useHashNavigation(sectionIds: string[]) {
  // Scroll to section based on URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sectionIds.includes(hash)) {
      const element = document.getElementById(hash);
      if (element) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [sectionIds]);

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && sectionIds.includes(hash)) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [sectionIds]);
}

/**
 * Enhanced scrollToSection that also updates the URL hash.
 */
export function useScrollToSectionWithHash() {
  const sectionIds = ['hero', 'stages', 'about', 'features', 'services', 'location', 'contact'];

  // Initialize hash navigation
  useHashNavigation(sectionIds);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Update URL hash without triggering scroll
      if (window.location.hash !== `#${id}`) {
        history.pushState(null, '', `#${id}`);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.location.hash) {
      history.pushState(null, '', window.location.pathname);
    }
  };

  return { scrollToSection, scrollToTop };
}
